'use client';

import { useCallback, useMemo, useState } from 'react';
import { REQUIRED_SEEDING_TERM_IDS, SEEDING_TERMS, type SeedingTermId } from './seedingTerms';

export interface UseSeedingAgreementResult {
  /** 항목별 체크 상태 (안내 항목은 항상 false, 사용 안 함). */
  checked: Record<SeedingTermId, boolean>;
  /** 필수 4종 전부 체크 여부 (게이트 기준). */
  allRequiredAgreed: boolean;
  /** "전체 동의하기" master 체크 상태 = 필수 전부 체크. */
  allChecked: boolean;
  /** 개별 항목 토글 (필수 항목만 의미 있음). */
  toggle: (id: SeedingTermId) => void;
  /** 전체 동의 토글 — 하나라도 미체크면 전부 on, 전부 체크면 전부 off. */
  toggleAll: () => void;
  /** 현재 열린 약관 시트 id (없으면 null). */
  openTermId: SeedingTermId | null;
  openTerm: (id: SeedingTermId) => void;
  closeTerm: () => void;
}

const INITIAL_CHECKED = SEEDING_TERMS.reduce(
  (acc, term) => {
    acc[term.id] = false;
    return acc;
  },
  {} as Record<SeedingTermId, boolean>,
);

/**
 * 시딩 신청/수락 동의 상태 훅 — 신청 라우트/수락 컨테이너에서 사용.
 * 필수 4종(`REQUIRED_SEEDING_TERM_IDS`) 전부 체크 시 게이트 통과.
 */
export const useSeedingAgreement = (): UseSeedingAgreementResult => {
  const [checked, setChecked] = useState<Record<SeedingTermId, boolean>>(INITIAL_CHECKED);
  const [openTermId, setOpenTermId] = useState<SeedingTermId | null>(null);

  const allRequiredAgreed = useMemo(
    () => REQUIRED_SEEDING_TERM_IDS.every((id) => checked[id]),
    [checked],
  );

  const toggle = useCallback((id: SeedingTermId) => {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleAll = useCallback(() => {
    setChecked((prev) => {
      const next = REQUIRED_SEEDING_TERM_IDS.every((id) => prev[id]);
      const updated = { ...prev };
      for (const id of REQUIRED_SEEDING_TERM_IDS) {
        updated[id] = !next;
      }
      return updated;
    });
  }, []);

  const openTerm = useCallback((id: SeedingTermId) => setOpenTermId(id), []);
  const closeTerm = useCallback(() => setOpenTermId(null), []);

  return {
    checked,
    allRequiredAgreed,
    allChecked: allRequiredAgreed,
    toggle,
    toggleAll,
    openTermId,
    openTerm,
    closeTerm,
  };
};
